<template>
	<div class="search-location" :class="[insideDialog && 'search-location--inside-dialog']">
		<v-combobox
			ref="input"
			v-model="model"
			v-mutate.attr="fixInaccessibleName"
			:search-input.sync="search"
			:items="locations"
			:loading="!!loading"
			@keyup.enter="model != search ? (model = search) : null"
			@blur="blur()"
			item-text="name"
			:placeholder="placeholder"
			no-data-text="Ingen treff"
			solo
			no-filter
			full-width
			:prepend-inner-icon="prependInnerIcon"
			append-icon="keyboard_arrow_down"
			hide-details
			clearable
			:autofocus="autofocus"
			attach=".search-location__results"
			:menu-props="$vuetify.breakpoint.mdAndDown ? { maxHeight: 'unset', maxWidth: '100%' } : undefined"
		>
			<template v-slot:item="{ parent, item /* , tile */ }">
				<span class="px-2">
					<v-icon left>{{ item.getSuggestionTypeIcon() }}</v-icon>
					{{ item.name }}
				</span>
			</template>
		</v-combobox>

		<div class="search-location__results white elevation-1" :style="{ width: insideDialog ? 'calc(100% + 52px)' : '100%' }"></div>
	</div>
</template>

<script>
import { debounce } from 'lodash-es';
import SearchService from '@/services/search';
import { GeoLocation } from '@/services/models';
import { getCurrentLocation } from '@fixit/fixit-no-header/src/utils/location'
const myLocation = new GeoLocation({
	name: 'Bruk min lokasjon',
	types: ['my_location'],
	type: 'NearYou'
});

export default {
	name: 'search-location',
	props: {
		autofocus: Boolean,
		prependInnerIcon: null,
		insideDialog: Boolean,
		placeholder: {
			type: String,
			default: 'Søk etter sted eller salong'
		},
		value: null
	},
	data() {
		return {
			focusIsSet: false,
			blurTime: 0,
			loading: 0,
			locations: [].concat(navigator.geolocation ? myLocation : []),
			search: ''
		};
	},
	computed: {
		model: {
			get() {
				return this.value;
			},

			set: debounce(async function(value) {
				// Fix for blur event [race-condition] unwantingly invoking the `model.set`.
				if (performance.now() - this.blurTime < 500) return;

				// If using "my location", look up current position and submit location
				if (value instanceof GeoLocation && value.types.includes('my_location') && !value.latitude && !value.longitude) {
					if (navigator.geolocation) {
						this.loading++;
						const { formatted_address, geometry } = await getCurrentLocation(this);
						const { lat: latitude, lng: longitude } = Object(Object(geometry).location);
						if (formatted_address && latitude && longitude) {
							value = new GeoLocation({
								...myLocation,
								latitude,
								longitude,
								name: formatted_address
							});
							this.$emit('selected', value);
						}
						this.loading--;
					}
				}
				// Else, look up lat/long for searched position and submit location
				else if (value) {
					if (value.name && (!value.latitude || !value.longitude)) {
						const { lat, lng } = Object(Object(Object(await this.$store.dispatch('search/getLocationFromAddress', value.name)).geometry).location);
						if (lat && lng) {
							value.latitude = lat;
							value.longitude = lng;
						}

						this.$emit('selected', value);
					} else if (value.type == 'salon') {
						// Salon search
						this.$emit('selected', value);
					} else {
						// Global search
						this.$emit('selected', value, { isGlobalSearch: true });
					}
				}

				this.$refs.input.blur();
				this.$emit('input', value);

				if (value) {
					// A location has been selected, clear debounce queue.
					setTimeout(() => {
						this.getLocations.cancel();
					});
				}
			}, 100)
		}
	},
	methods: {
		blur() {
			setTimeout(() => {
				this.blurTime = self.performance.now();
			}, 100);
		},

		setFocus() {
			this.$vuetify.goTo(0);
			if (!this.focusIsSet) {
				setTimeout(
					() => {
						this.focusIsSet = true;
						this.$refs.input.focus();
						this.$refs.input.isMenuActive = true;
					},
					(window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0) === 0 ? 0 : 550
				);
			}
		},

		/**
		 * The `clear` action for the v-combobox has an inaccessible name. This is a temporary fix until Vuetify add the missing aria-label to it.
		 * https://web.dev/button-name/
		 */
		fixInaccessibleName() {
			this.$nextTick(() => {
				if (this.$refs.input && !this.focusIsSet) {
					this.$refs.input.$el.querySelector('.v-input__append-inner .v-icon').setAttribute('aria-label', 'Tøm søkefelt');
				}
			});
		}
	},
	watch: {
		search(value) {
			if (value) {
				this.getLocations();
			}

			// this.$emit('input', value);
		}
	},
	created() {
		this.getLocations = debounce(async function() {
			if (this.search) {
				this.loading++;
				this.locations = (await SearchService.GetLocations(this.search)).concat(navigator.geolocation ? myLocation : []);
				this.loading--;
			}
		}, 1000);

		this.$nextTick(() => {
			this.$eventBus.$on('search-location--focus', this.setFocus);
			if (this.$refs.input) {
				this.$refs.input.$el.querySelector('input').setAttribute('aria-label', 'Søkefelt'); // Search field is missing associated label (inaccessible): https://www.w3.org/TR/WCAG20-TECHS/ARIA14.html
			}
		});
	},
	beforeDestroy() {
		this.$eventBus.$off('search-location--focus', this.setFocus);
	}
};
</script>

<style lang="scss">
.search-location {
	.v-input {
		&__slot {
			padding: 0 1rem !important;
			box-shadow: unset !important;
			border-radius: 0;
		}

		input {
			padding: 0 1rem;
		}
	}
}
</style>

<style lang="scss" scoped>
@import '~@/styles/variables';

.search-location {
	position: relative;

	&__results {
		position: absolute;
		left: 0;
		z-index: 10;

		&::v-deep .v-menu__content {
			position: static;
			box-shadow: unset;
			width: 100%;
		}

		.search-location--inside-dialog & {
			left: -52px;
		}
	}

	&--inside-dialog {
		&::v-deep .v-input__slot {
			padding-left: 0 !important;

			input {
				padding-left: 8px;
			}
		}
	}
}
</style>
